import { useEffect } from 'react';
import './CloseCombatAnimation.css';

/**
 * PROFESSIONAL CLOSE COMBAT ANIMATION
 * Fighting-type physical move with rapid multi-hit combo attacks
 * 
 * Key Features:
 * - Fighting-type physical attack (120 base power, 100% accuracy)
 * - Lowers user's Defense and Special Defense by 1 stage each
 * - Rapid combo sequence with multiple strike effects
 * - Professional gaming company quality with martial arts timing
 * - Physical move animation with close-range combat mechanics
 */
export default function CloseCombatAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.closest('.battle-container') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate combat distance
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    // Elements to clean up
    const elements = [];

    // PHASE 1: Combat Stance Preparation (0-400ms)
    console.log('Close Combat Phase 1: Combat Stance Preparation');
    
    // Player combat stance with fighting aura
    playerEl.classList.add('close-combat-user-stance');
    
    // Fighting energy aura around player
    const fightingAura = document.createElement('div');
    fightingAura.className = 'close-combat-fighting-aura';
    fightingAura.style.left = `${playerX - 90}px`;
    fightingAura.style.top = `${playerY - 90}px`;
    fightingAura.style.width = '180px';
    fightingAura.style.height = '180px';
    container.appendChild(fightingAura);
    elements.push(fightingAura);

    // Combat energy particles around player
    for (let i = 0; i < 16; i++) {
      const combatParticle = document.createElement('div');
      combatParticle.className = 'close-combat-energy-particle';
      const angle = (i / 16) * 360;
      const radius = 60 + (i % 4) * 12;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      combatParticle.style.left = `${particleX - 5}px`;
      combatParticle.style.top = `${particleY - 5}px`;
      combatParticle.style.animationDelay = `${i * 20}ms`;
      combatParticle.textContent = '👊';
      container.appendChild(combatParticle);
      elements.push(combatParticle);
    }

    // Combat readiness indicators
    for (let i = 0; i < 6; i++) {
      const readinessIndicator = document.createElement('div');
      readinessIndicator.className = 'close-combat-readiness';
      readinessIndicator.style.left = `${playerX + (i % 2 === 0 ? -30 : 30)}px`;
      readinessIndicator.style.top = `${playerY + (i * 8) - 25}px`;
      readinessIndicator.style.animationDelay = `${i * 50}ms`;
      readinessIndicator.textContent = '⚡';
      container.appendChild(readinessIndicator);
      elements.push(readinessIndicator);
    }

    // PHASE 2: Rapid Strike Combo (400-1200ms)
    const phase2Timer = setTimeout(() => {
      console.log('Close Combat Phase 2: Rapid Strike Combo');
      
      // Player rush toward opponent
      playerEl.classList.remove('close-combat-user-stance');
      playerEl.classList.add('close-combat-user-rush');
      playerEl.style.setProperty('--rush-x', `${dx * 0.8}px`);
      playerEl.style.setProperty('--rush-y', `${dy * 0.8}px`);

      // Create multiple strike effects (5 rapid strikes)
      const strikes = [
        { delay: 0, type: 'jab', emoji: '👊', offset: { x: -15, y: -10 } },
        { delay: 120, type: 'cross', emoji: '🥊', offset: { x: 15, y: -5 } },
        { delay: 240, type: 'hook', emoji: '👊', offset: { x: -20, y: 5 } },
        { delay: 360, type: 'uppercut', emoji: '🥊', offset: { x: 0, y: -20 } },
        { delay: 480, type: 'combo', emoji: '💥', offset: { x: 0, y: 0 } }
      ];

      strikes.forEach((strike, index) => {
        const strikeTimer = setTimeout(() => {
          // Strike impact effect
          const strikeImpact = document.createElement('div');
          strikeImpact.className = `close-combat-strike-${strike.type}`;
          strikeImpact.style.left = `${opponentX + strike.offset.x - 25}px`;
          strikeImpact.style.top = `${opponentY + strike.offset.y - 25}px`;
          strikeImpact.style.width = '50px';
          strikeImpact.style.height = '50px';
          strikeImpact.style.animationDelay = `${index * 20}ms`;
          container.appendChild(strikeImpact);
          elements.push(strikeImpact);

          // Strike visual effect
          const strikeVisual = document.createElement('div');
          strikeVisual.className = 'close-combat-strike-visual';
          strikeVisual.style.left = `${opponentX + strike.offset.x - 8}px`;
          strikeVisual.style.top = `${opponentY + strike.offset.y - 8}px`;
          strikeVisual.style.animationDelay = `${index * 15}ms`;
          strikeVisual.textContent = strike.emoji;
          container.appendChild(strikeVisual);
          elements.push(strikeVisual);

          // Opponent hit reaction for each strike
          if (index < 4) {
            opponentEl.classList.add(`close-combat-hit-${index + 1}`);
            setTimeout(() => {
              opponentEl.classList.remove(`close-combat-hit-${index + 1}`);
            }, 100);
          }

          // Impact particles for each strike
          for (let i = 0; i < 6; i++) {
            const impactParticle = document.createElement('div');
            impactParticle.className = 'close-combat-impact-particle';
            const particleAngle = (i / 6) * 360;
            const particleRadius = 20 + Math.random() * 15;
            const particleX = opponentX + strike.offset.x + Math.cos(particleAngle * Math.PI / 180) * particleRadius;
            const particleY = opponentY + strike.offset.y + Math.sin(particleAngle * Math.PI / 180) * particleRadius;
            impactParticle.style.left = `${particleX - 3}px`;
            impactParticle.style.top = `${particleY - 3}px`;
            impactParticle.style.animationDelay = `${i * 25}ms`;
            impactParticle.style.setProperty('--scatter-x', `${Math.cos(particleAngle * Math.PI / 180) * 25}px`);
            impactParticle.style.setProperty('--scatter-y', `${Math.sin(particleAngle * Math.PI / 180) * 25}px`);
            impactParticle.textContent = '💢';
            container.appendChild(impactParticle);
            elements.push(impactParticle);
          }
        }, strike.delay);
        elements.push({ timer: strikeTimer, type: 'timer' });
      });
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Final Devastating Blow (1200-1600ms)
    const phase3Timer = setTimeout(() => {
      console.log('Close Combat Phase 3: Final Devastating Blow');
      
      // Final massive hit reaction
      opponentEl.classList.add('close-combat-final-hit');

      // Reset player position with final strike effect
      playerEl.classList.remove('close-combat-user-rush');
      playerEl.classList.add('close-combat-user-finisher');

      // Massive combat explosion
      const finalExplosion = document.createElement('div');
      finalExplosion.className = 'close-combat-final-explosion';
      finalExplosion.style.left = `${opponentX - 120}px`;
      finalExplosion.style.top = `${opponentY - 120}px`;
      finalExplosion.style.width = '240px';
      finalExplosion.style.height = '240px';
      container.appendChild(finalExplosion);
      elements.push(finalExplosion);

      // Combat shockwaves
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'close-combat-shockwave';
          shockwave.style.left = `${opponentX - 80}px`;
          shockwave.style.top = `${opponentY - 80}px`;
          shockwave.style.width = '160px';
          shockwave.style.height = '160px';
          shockwave.style.animationDelay = `${i * 100}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 120);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }

      // Final impact burst
      for (let i = 0; i < 15; i++) {
        const burstParticle = document.createElement('div');
        burstParticle.className = 'close-combat-final-burst';
        const burstAngle = (i / 15) * 360;
        const burstRadius = 70 + Math.random() * 40;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius;
        burstParticle.style.left = `${burstX - 6}px`;
        burstParticle.style.top = `${burstY - 6}px`;
        burstParticle.style.animationDelay = `${i * 25}ms`;
        burstParticle.style.setProperty('--burst-x', `${Math.cos(burstAngle * Math.PI / 180) * 35}px`);
        burstParticle.style.setProperty('--burst-y', `${Math.sin(burstAngle * Math.PI / 180) * 35}px`);
        burstParticle.textContent = '💥';
        container.appendChild(burstParticle);
        elements.push(burstParticle);
      }
    }, 1200);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Defense Reduction Aftermath (1600-2000ms)
    const phase4Timer = setTimeout(() => {
      console.log('Close Combat Phase 4: Defense Reduction Aftermath');
      
      // Remove combat effects
      playerEl.classList.remove('close-combat-user-finisher');
      playerEl.classList.add('close-combat-user-exhausted');
      
      // Defense reduction effect on player (lowers Def & Sp.Def)
      const defenseReduction = document.createElement('div');
      defenseReduction.className = 'close-combat-defense-reduction';
      defenseReduction.style.left = `${playerX - 70}px`;
      defenseReduction.style.top = `${playerY - 70}px`;
      defenseReduction.style.width = '140px';
      defenseReduction.style.height = '140px';
      container.appendChild(defenseReduction);
      elements.push(defenseReduction);

      // Exhaustion particles around player
      for (let i = 0; i < 10; i++) {
        const exhaustionParticle = document.createElement('div');
        exhaustionParticle.className = 'close-combat-exhaustion-particle';
        const exhaustionX = playerX + (Math.random() - 0.5) * 80;
        const exhaustionY = playerY + (Math.random() - 0.5) * 60;
        exhaustionParticle.style.left = `${exhaustionX - 4}px`;
        exhaustionParticle.style.top = `${exhaustionY - 4}px`;
        exhaustionParticle.style.animationDelay = `${i * 35}ms`;
        exhaustionParticle.textContent = '😮‍💨';
        container.appendChild(exhaustionParticle);
        elements.push(exhaustionParticle);
      }

      // Defense down indicator
      const defenseDownIndicator = document.createElement('div');
      defenseDownIndicator.className = 'close-combat-defense-down';
      defenseDownIndicator.style.left = `${playerX + 25}px`;
      defenseDownIndicator.style.top = `${playerY - 35}px`;
      defenseDownIndicator.textContent = 'DEF ↓ SP.DEF ↓';
      container.appendChild(defenseDownIndicator);
      elements.push(defenseDownIndicator);
    }, 1600);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (2000ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Close Combat: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('close-combat-user-stance', 'close-combat-user-rush', 'close-combat-user-finisher', 'close-combat-user-exhausted');
      opponentEl.classList.remove('close-combat-final-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 2000);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('close-combat-user-stance', 'close-combat-user-rush', 'close-combat-user-finisher', 'close-combat-user-exhausted');
      opponentEl.classList.remove('close-combat-final-hit');
      // Remove hit classes
      for (let i = 1; i <= 4; i++) {
        opponentEl.classList.remove(`close-combat-hit-${i}`);
      }
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
CloseCombatAnimation.moveType = 'fighting';
